import React, {useEffect, useState} from "react";
import {Alert, Modal, Button, Form, Spinner} from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import { useSubheader } from "@metronic/layout";
import { useHistory } from "react-router-dom";
import Pagination from "react-js-pagination";

import { save } from "@services/hospitalization";
import { getCasesByStatuses } from "@services/cases";
import GeneralTable from "@components/shared/general-table";
import GeneralFilter from "@components/shared/general-filter";
import CaseContext from "@contexts/case-context";
import {getInputClasses} from "@utils/get-input-classes";
import * as Yup from "yup";
import {update} from "@services/users";
import swal from "sweetalert";
import {getAll as getAllHospitals} from "@services/hospitals";
import {getProgrammingSurgeries} from "@services/programmings";
import {useDocumentIntake, linkAnalysesToCase} from "@components/document-intake/use-document-intake";
import IntakeSummary, {IntakeHaloStyles} from "@components/document-intake/intake-summary";
import {buildPrefillWithContext, getIntakeNotices, getHaloClass} from "@components/document-intake/document-intake-prefill";
import {updateCaseDetails} from "@services/case-details";

// Datos clave que pide el formulario de consulta de programacion; la IA puede llenar casi todos.
const LOOKUP_KEY_FIELDS = [
  {name: "hospitalId", label: "Hospital"},
  {name: "atentionIden", label: "Atención"},
  {name: "identificator", label: "Folio"},
  {name: "policy", label: "Póliza"},
  {name: "assuredFlastname", label: "Apellido paterno"},
];

const EMPTY_INTAKE = {analyses: [], prefill: {values: {}, sources: {}}, warnings: [], ready: false};

const isFilled = (value) => value !== undefined && value !== null && String(value).trim() !== "";

const HospitalizationCreate = () => {
  const subheader = useSubheader();

  const history = useHistory();
  const [casesRecord, setCasesRecord] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [paramsPagination, setParamsPagination] = useState({
    activePage: 1,
    totalPages: null,
    itemsCountPerPage: null,
    totalItemsCount: 0,
  });
  const [showSurgeryModal, setShowSurgeryModal] = useState(false);
  const [selectedOptionSurgery, setSelectedOptionSurgery] = useState('');
  // Resultado de la lectura de documentos de cirugia programada, previo a consultar la programacion.
  const [intake, setIntake] = useState(EMPTY_INTAKE);
  // Antes de entrar al caso se piden (opcionales) los documentos del asegurado.
  const { requestDocuments, intakeModal } = useDocumentIntake();

  const validationSchema = Yup.object().shape({
    hospitalId: Yup.string()
        .required("El campo es requerido"),
    atentionIden: Yup.string()
        .required("El campo es requerido"),
    identificator: Yup.string()
        .required("El campo es requerido"),
    policy: Yup.string()
        .required("El campo es requerido")
        .matches(/^[a-zA-Z0-9]*$/, "Solo se permiten letras y números"),
  });

  useEffect(() => {
    getAllHospitals().then( result => setHospitals(result.data));

    subheader.setTitle("Ingreso hospitalario - Nueva solicitud");
  }, []);

  const getAllCasesByStatus = (pageNumber) => {
    getCasesByStatuses([], pageNumber).then((response) => {
      setCasesRecord(response.data);
      setParamsPagination({
        activePage: pageNumber,
        totalPages: response.totalPages,
        itemsCountPerPage: response.size,
        totalItemsCount: response.totalElements,
      });
    });
  }

  const handleShowSurgeryModal = () => {
    setIntake(EMPTY_INTAKE);
    setShowSurgeryModal(!showSurgeryModal);
  }

  /** Captura manual: sin documentos ni IA, directo al formulario de consulta. */
  const startManualSurgeryCapture = () => setIntake({...EMPTY_INTAKE, ready: true});

  const startSurgeryIntake = () =>
    requestDocuments("INGRESO_CIRUGIA_PROGRAMADA", (analyses, context) =>
      setIntake({
        analyses,
        prefill: buildPrefillWithContext(analyses, context),
        warnings: getIntakeNotices(analyses, { birthdate: context.birthdate }),
        ready: true,
      })
    );

  /** Lo que se captura en el formulario de consulta tambien pasa al caso, junto con lo que leyo la IA. */
  const getCaseValuesFromLookup = (values) => {
    const caseValues = {...intake.prefill.values};
    ["atentionIden", "identificator", "policy", "assuredFlastname"].forEach((name) => {
      if (isFilled(values[name])) {
        caseValues[name] = String(values[name]).trim();
      }
    });
    if (isFilled(values.hospitalId)) {
      caseValues.hospitalId = parseInt(values.hospitalId, 10);
    }
    return caseValues;
  }

  /** Etiqueta el caso como creado con apoyo de IA; nunca detiene la navegacion del usuario. */
  const markAutomaticCreation = (caseId, analyses) =>
    caseId && analyses.length > 0
      ? updateCaseDetails(caseId, {automaticCreation: true}).catch(() => {})
      : Promise.resolve();

  const createCase = (includeScheduledSurgery = false, scheduledSurgery = false, analyses = [], caseValues = {}) => {
    let queryParams= "";

    if (includeScheduledSurgery && scheduledSurgery) {
      queryParams = `?cirugia_programada=${scheduledSurgery}&nueva=false`;
    } else if (includeScheduledSurgery && !scheduledSurgery) {
      queryParams = `?cirugia_programada=${scheduledSurgery}&nueva=true`;
    }

    save({ caseStatusId: 1, ...caseValues })
      .then((response) => {
        const { slug, id } = response.data;
        return linkAnalysesToCase(slug, analyses)
          .then(() => markAutomaticCreation(id, analyses))
          .then(() => {
            history.push(`/ingreso_hospitalario_detalles/${slug}${queryParams}`);
          });
      })
      .catch((error) => {
        // swal("Good job!", "Ocurrio algun error", "error");
      });
  }

  const handlePageChange = async (pageNumber) => {
    setParamsPagination({ ...paramsPagination, activePage: pageNumber, updatePage: true });

    if (!paramsPagination.hasOwnProperty("fromFilter")) {
      getAllCasesByStatus(pageNumber);
    }
  }

  const onSubmit = (values, { setStatus, setSubmitting }) => {
    setTimeout(() => {
      getProgrammingSurgeries(values).then(response => {
        const results = response.data;

        if (results.length === 0) {
          swal({
            title: '',
            text: 'Asegurado no cuenta con Programación de Cirugía',
            icon: 'warning',
            buttons: {
              cancel: "Cancel",
              confirm: {
                text: "Continuar",
                value: true,
                visible: true,
                className: "",
                closeModal: true
              }
            },
          }).then((willContinue) => {
            if (willContinue) {
              createCase(true, false, intake.analyses, getCaseValuesFromLookup(values));
            }
          });
        } else if (results[0]?.caseStatusId !== 1) {
          swal({
            title: 'Asegurado con Cirugía Programada',
            text: `Esta Solicitud ya se encuentra en seguimiento con el siguiente folio: ${results[0]?.caseTicket}`,
            icon: 'success',
          });
        }
        else {
          const fullName = `${results[0]?.assuredName} ${results[0]?.assuredFlastname} ${results[0]?.assuredMlastname}`;
          swal({
            title: 'Asegurado con Cirugía Programada',
            text: `Asegurado: ${fullName}\nHospital: ${results[0].hospital?.hospitalName}\nTipo de ingreso: ${results[0]?.incomeType}`,
            icon: 'success',
            buttons: {
              cancel: "Cancel",
              confirm: {
                text: "Continuar",
                value: true,
                visible: true,
                className: "",
                closeModal: true
              }
            },
          }).then((willContinue) => {
            if (willContinue) {
              linkAnalysesToCase(results[0].slug, intake.analyses)
                .then(() => markAutomaticCreation(results[0].id, intake.analyses))
                .then(() => {
                  history.push(`/ingreso_hospitalario_detalles/${results[0].slug}?cirugia_programada=true&nueva=false`);
                });
            }
          });
        }
      });
      setSubmitting(false);
    }, 1000);
  }

  return (
    <>
      <Alert
        variant="success"
        className="text-center"
        style={{ fontSize: "16px" }}
      >
        Ingreso Hospitalario
      </Alert>

      <Modal show={showSurgeryModal} onHide={handleShowSurgeryModal}>
        <Modal.Header closeButton>
          <Modal.Title>¿Cirugía programada?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="d-flex align-items-center">
            <Form.Check
                type="radio"
                id="radio-yes"
                label="Sí"
                name="isScheduledSurgery"
                value="Y"
                checked={selectedOptionSurgery === 'Y'}
                onChange={(e) => {
                  setSelectedOptionSurgery(e.target.value);
                  setIntake(EMPTY_INTAKE);
                }}
            />
            <Form.Check
                className="ml-5"
                type="radio"
                id="radio-no"
                label="No"
                name="isScheduledSurgery"
                value="N"
                checked={selectedOptionSurgery === 'N'}
                onChange={(e) => {
                  setSelectedOptionSurgery(e.target.value);
                  setIntake(EMPTY_INTAKE);
                }}
            />
          </Form.Group>

          {selectedOptionSurgery === 'Y' && !intake.ready && (
            <div className="mt-2">
              <p className="text-muted small">
                Sube la carta de autorización y la identificación del paciente (o de su padre o
                tutor si es menor): nuestra inteligencia artificial las leerá para prellenar los
                datos. O captura todo a mano.
              </p>
              <Button variant="primary" onClick={startSurgeryIntake}>
                <i className="fa fa-magic mr-2" />
                Subir documentos con IA
              </Button>
              <Button variant="outline-secondary" className="ml-2" onClick={startManualSurgeryCapture}>
                Captura manual
              </Button>
            </div>
          )}

            <IntakeHaloStyles />
            <Formik onSubmit={onSubmit}
                    validationSchema={validationSchema}
                    enableReinitialize={true}
                    initialValues={{
                      hospitalId: "",
                      atentionIden: intake.prefill.values.atentionIden || "",
                      identificator: intake.prefill.values.identificator || "",
                      policy: intake.prefill.values.policy || "",
                      assuredFlastname: intake.prefill.values.assuredFlastname || "",
                    }}>
              {({values, errors, touched, handleChange, handleSubmit, status, setFieldValue, isSubmitting}) => {
                const aiAssisted = intake.analyses.length > 0;
                const halo = (name) => aiAssisted
                  ? getHaloClass({
                    filled: isFilled(values[name]),
                    fromAI: intake.prefill.sources[name] !== undefined,
                  })
                  : "";
                return (
                  <>
                    <Form onSubmit={handleSubmit} hidden={selectedOptionSurgery !== 'Y' || !intake.ready}>
                      {aiAssisted && (
                        <IntakeSummary
                          items={LOOKUP_KEY_FIELDS.map((field) => ({
                            label: field.label,
                            filled: isFilled(values[field.name]),
                          }))}
                          warnings={intake.warnings}
                        />
                      )}
                      <Form.Group controlId="hospitalId">
                        <Form.Label>Hospital</Form.Label>
                        <Form.Control as="select"
                                      name="hospitalId"
                                      onChange={handleChange}
                                      className={`${getInputClasses(touched, errors, "hospitalId")} ${halo("hospitalId")}`}>
                          <option value="">Selecciona una opcion...</option>
                          {hospitals?.map( (hospital, idx) => (
                              <option key={idx} value={hospital.id}>{hospital.hospitalName}</option>
                          ))}
                          <ErrorMessage name="hospitalId">
                            { msg => (
                                <Form.Control.Feedback type="invalid">
                                  {msg}
                                </Form.Control.Feedback>
                            )}
                          </ErrorMessage>
                        </Form.Control>
                      </Form.Group>

                      <Form.Group controlId="atentionIden">
                        <Form.Label>Atención</Form.Label>
                        <Form.Control type="text"
                                      value={values.atentionIden}
                                      onChange={handleChange}
                                      maxLength={10}
                                      name="atentionIden"
                                      className={`${getInputClasses(touched, errors, "atentionIden")} ${halo("atentionIden")}`} />

                        <ErrorMessage name="atentionIden">
                          { msg => (
                              <Form.Control.Feedback type="invalid">
                                {msg}
                              </Form.Control.Feedback>
                          )}
                        </ErrorMessage>
                      </Form.Group>

                      <Form.Group controlId="identificator">
                        <Form.Label>Folio</Form.Label>
                        <Form.Control type="text"
                                      value={values.identificator}
                                      onChange={handleChange}
                                      maxLength={10}
                                      name="identificator"
                                      className={`${getInputClasses(touched, errors, "identificator")} ${halo("identificator")}`} />

                        <ErrorMessage name="identificator">
                          { msg => (
                              <Form.Control.Feedback type="invalid">
                                {msg}
                              </Form.Control.Feedback>
                          )}
                        </ErrorMessage>
                      </Form.Group>

                      <Form.Group controlId="policy">
                        <Form.Label>Poliza</Form.Label>
                        <Form.Control type="text"
                                      value={values.policy}
                                      onChange={handleChange}
                                      maxLength={8}
                                      name="policy"
                                      className={`${getInputClasses(touched, errors, "policy")} ${halo("policy")}`} />

                        <ErrorMessage name="policy">
                          { msg => (
                              <Form.Control.Feedback type="invalid">
                                {msg}
                              </Form.Control.Feedback>
                          )}
                        </ErrorMessage>
                      </Form.Group>

                      <Form.Group controlId="assuredFlastname">
                        <Form.Label>Apellido paterno</Form.Label>
                        <Form.Control type="text"
                                      value={values.assuredFlastname}
                                      onChange={handleChange}
                                      name="assuredFlastname"
                                      className={`${getInputClasses(touched, errors, "assuredFlastname")} ${halo("assuredFlastname")}`} />

                        <ErrorMessage name="assuredFlastname">
                          { msg => (
                              <Form.Control.Feedback type="invalid">
                                {msg}
                              </Form.Control.Feedback>
                          )}
                        </ErrorMessage>
                      </Form.Group>

                      <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting && <Spinner as="span" animation="border" variant="dark" size="sm" />}
                        Continuar
                      </Button>
                    </Form>
                  </>
                );
              }}
          </Formik>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleShowSurgeryModal}>
            Cancelar
          </Button>
          <Button variant="outline-secondary" onClick={ event => {
            setShowSurgeryModal(false);
            createCase(false, false, []);
          }} hidden={selectedOptionSurgery !== 'N'}>
            Captura manual
          </Button>
          <Button variant="primary" onClick={ event => {
            setShowSurgeryModal(false);
            requestDocuments("INGRESO_REPORTE_HOSPITALARIO", (analyses, context) =>
              createCase(false, false, analyses, buildPrefillWithContext(analyses, context).values));
          }} hidden={selectedOptionSurgery !== 'N'}>
            <i className="fa fa-magic mr-2" />
            Reporte hospitalario con IA
          </Button>
        </Modal.Footer>
      </Modal>

      {intakeModal}

      <CaseContext.Provider value={[
        casesRecord, setCasesRecord,
        paramsPagination, setParamsPagination
      ]}>
        <GeneralFilter
          result={casesRecord}
          statuses={[]}
          hideAxaCategoryId={true}
          hidePeriodFrom={true}
          hidePeriodTo={true}
          hideInsurance={true}
          hideHospital={true}
          hideMovementType={true}
          hidePresumptiveDiagnosis={true}
          hideCategoryVip={true}
          hideComprehensiveCare={true}
          hideTypePolicy={true}
          hideAtcNumber={true}
          textButton="Consultar"
          hideButtonCreateCase={false}
          createCase={handleShowSurgeryModal}
        />
      </CaseContext.Provider>

      <GeneralTable
        urlToGo="/seguimiento_detalles/"
        data={casesRecord}
        activePage={paramsPagination.activePage}
        totalItemsCount={paramsPagination.totalItemsCount}
        hideReloadPageButton={true}
        hideOptionsColumn={true}
        hideFlagsColumn={true}
        hideCounterColumn={true}
        hideAdmissionDateTimeColumn={true}
        hideDiagnosticColumn={true}
        hideReasonColumn={true}
        hideAssignedDoctorColumn={true}
        hideAssignedNurseColumn={true}
        hideAssignedConciergeColumn={true}
        hideAxaCategoryColumn={true}
        hideDaysStayColumn={true}
        hideMountsColumn={true}
        hideHospitalDateTimeHireColumn={true}
        hidePatientStatusColumn={true}
        hideTabulationColumn={true}
        assignationColumn=""
        reason="Tracing"
        currModule="Seguimiento"
      />

      <div className="d-flex justify-content-center">
        <Pagination
          activePage={paramsPagination.activePage}
          itemsCountPerPage={paramsPagination.itemsCountPerPage}
          totalItemsCount={paramsPagination.totalItemsCount}
          pageRangeDisplayed={10}
          itemClass="page-item"
          linkClass="btn btn-light"
          onChange={handlePageChange}
        />
      </div>
    </>
  );
};

export default HospitalizationCreate;

